import { Notification, NotificationAction } from '@/components/notifications/types';

export const state = {
  notifications: [] as Notification[],
};

export const getters = {
  notifications(state): Notification[] {
    return state.notifications;
  },
  unreadNotifications(state): Notification[] {
    return state.notifications.filter(n => !n.read);
  },
  notificationCount(state): number {
    return state.notifications.length;
  },
  unreadCount(state): number {
    return state.notifications.filter(n => !n.read).length;
  },
};

export const mutations = {
  REMOVE_NOTIFICATION(state, id: string) {
    const index = state.notifications.findIndex(n => n.id === id);
    if (index !== -1) {
      state.notifications.splice(index, 1);
    }
  },
  FLUSH_NOTIFICATIONS(state) {
    state.notifications = [];
  },
  MARK_NOTIFICATION_READ(state, id: string) {
    const notification = state.notifications.find(n => n.id === id);
    if (notification) {
      notification.read = true;
    }
  },
  MARK_ALL_NOTIFICATIONS_READ(state) {
    state.notifications.forEach(n => {
      n.read = true;
    });
  },
  
  // Enhanced notification methods
  ADD_NOTIFICATION(state, notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) {
    const newNotification: Notification = {
      ...notification,
      id: generateId(),
      timestamp: Date.now(),
      read: false,
    };
    state.notifications.push(newNotification);
  },
  
  NOTIFY_SUCCESS(state, notifications: string | string[]) {
    const messages = Array.isArray(notifications) ? notifications : [notifications];
    messages.forEach(message => {
      mutations.ADD_NOTIFICATION(state, {
        type: 'success',
        title: message,
      });
    });
  },
  
  NOTIFY_ERROR(state, notifications: string | string[] | { errors?: Record<string, string[]> }) {
    const errorObj = notifications as { errors?: Record<string, string[]> };
    if (typeof notifications === 'object' && errorObj.errors) {
      Object.keys(errorObj.errors).forEach(key => {
        errorObj.errors![key].forEach((message: string) => {
          mutations.ADD_NOTIFICATION(state, {
            type: 'error',
            title: message,
          });
        });
      });
      return;
    }
    
    const messages = Array.isArray(notifications) ? notifications : [notifications];
    messages.forEach((message: string) => {
      mutations.ADD_NOTIFICATION(state, {
        type: 'error',
        title: message,
      });
    });
  },
  
  NOTIFY_WARNING(state, notifications: string | string[]) {
    const messages = Array.isArray(notifications) ? notifications : [notifications];
    messages.forEach((message: string) => {
      mutations.ADD_NOTIFICATION(state, {
        type: 'warning',
        title: message,
        duration: 4000, // 4 seconds
      });
    });
  },
  
  NOTIFY_INFO(state, notifications: string | string[]) {
    const messages = Array.isArray(notifications) ? notifications : [notifications];
    messages.forEach((message: string) => {
      mutations.ADD_NOTIFICATION(state, {
        type: 'info',
        title: message,
        duration: 4000, // 4 seconds
      });
    });
  },
  
  NOTIFY_CRITICAL(state, notifications: string | string[]) {
    const messages = Array.isArray(notifications) ? notifications : [notifications];
    messages.forEach((message: string) => {
      mutations.ADD_NOTIFICATION(state, {
        type: 'critical',
        title: message,
        persistent: true,
      });
    });
  },
};

export const actions = {
  // Enhanced notification actions
  addNotification({ commit }, notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) {
    commit('ADD_NOTIFICATION', notification);
  },
  
  notifySuccess({ commit }, message: string | string[]) {
    commit('NOTIFY_SUCCESS', message);
  },
  
  notifyError({ commit }, message: string | string[] | { errors?: Record<string, string[]> }) {
    commit('NOTIFY_ERROR', message);
  },
  
  notifyWarning({ commit }, message: string | string[]) {
    commit('NOTIFY_WARNING', message);
  },
  
  notifyInfo({ commit }, message: string | string[]) {
    commit('NOTIFY_INFO', message);
  },
  
  notifyCritical({ commit }, message: string | string[]) {
    commit('NOTIFY_CRITICAL', message);
  },
  
  dismissNotification({ commit }, id: string) {
    commit('REMOVE_NOTIFICATION', id);
  },
  
  dismissAllNotifications({ commit }) {
    commit('FLUSH_NOTIFICATIONS');
  },
  
  markAsRead({ commit }, id: string) {
    commit('MARK_NOTIFICATION_READ', id);
  },
  
  markAllAsRead({ commit }) {
    commit('MARK_ALL_NOTIFICATIONS_READ');
  },
};

// Helper function to generate unique IDs
function generateId(): string {
  return `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}
